<template>
  <!-- First Modal: Ask if they want to skip -->
  <div v-if="showFirstModal" class="skip-onboarding-modal fixed inset-0 overflow-y-auto z-[100010]" role="dialog" aria-modal="true">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm transition-opacity"></div>

    <div class="flex min-h-screen items-end justify-center p-4 text-center sm:items-center sm:p-0">
      <div class="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
        <div class="sm:flex sm:items-start">
          <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 sm:mx-0 sm:h-10 sm:w-10">
            <svg class="h-6 w-6 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
          </div>
          <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <h3 class="text-base font-semibold leading-6 text-gray-900 dark:text-white">Skip Introduction Tour?</h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Do you want to skip the introduction tour? You can always explore the app on your own.
              </p>
            </div>
          </div>
        </div>
        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            @click="handleFirstConfirm"
            class="inline-flex w-full justify-center rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 sm:ml-3 sm:w-auto"
          >
            Skip Tour
          </button>
          <button
            type="button"
            @click="handleCancel"
            class="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
          >
            Continue Tour
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Second Modal: Final warning -->
  <div v-if="showSecondModal" class="skip-onboarding-modal fixed inset-0 overflow-y-auto z-[100010]" role="dialog" aria-modal="true">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm transition-opacity"></div>

    <div class="flex min-h-screen items-end justify-center p-4 text-center sm:items-center sm:p-0">
      <div class="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
        <div class="sm:flex sm:items-start">
          <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
            <svg class="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <h3 class="text-base font-semibold leading-6 text-gray-900 dark:text-white">Are You Sure?</h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500 dark:text-gray-400">
                This tour <span class="font-semibold text-red-600 dark:text-red-400">will not be shown again</span>. 
                We recommend completing it to get the most out of TrackFolio.
              </p>
            </div>
          </div>
        </div>
        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            @click="handleFinalSkip"
            class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
          >
            Skip Permanently
          </button>
          <button
            type="button"
            @click="handleGoBack"
            class="mt-3 inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 sm:mt-0 sm:w-auto"
          >
            Continue Tour
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useOnboarding } from '@/composables/useOnboarding';

export default {
  name: 'SkipOnboardingModal',
  setup() {
    const { 
      showSkipModal, 
      hideSkipModal, 
      confirmSkipOnboarding 
    } = useOnboarding();
    
    const showFirstModal = ref(false);
    const showSecondModal = ref(false);

    // Watch for when skip is requested
    const checkSkipModal = () => {
      if (showSkipModal.value) {
        showFirstModal.value = true;
        showSecondModal.value = false;
      }
    };

    // Expose method to be called from composable
    const openModal = () => {
      showFirstModal.value = true;
      showSecondModal.value = false;
    };

    const handleFirstConfirm = () => {
      showFirstModal.value = false;
      showSecondModal.value = true;
    };

    const handleCancel = () => {
      showFirstModal.value = false;
      showSecondModal.value = false;
      hideSkipModal();
    };

    const handleGoBack = () => {
      showFirstModal.value = false;
      showSecondModal.value = false;
      hideSkipModal();
    };

    const handleFinalSkip = () => {
      showFirstModal.value = false;
      showSecondModal.value = false;
      confirmSkipOnboarding();
    };

    return {
      showFirstModal,
      showSecondModal,
      showSkipModal,
      openModal,
      handleFirstConfirm,
      handleCancel,
      handleGoBack,
      handleFinalSkip,
      checkSkipModal
    };
  },
  watch: {
    showSkipModal: {
      handler(newVal) {
        if (newVal) {
          // Ensure the warning modal is fully interactive even if Driver.js is active underneath.
          // We add a body class so CSS can disable pointer-events on Driver elements.
          try {
            document.body.classList.add('skip-onboarding-active');
          } catch (e) {
            // no-op (e.g., non-browser environments)
          }
          this.showFirstModal = true;
          this.showSecondModal = false;
        } else {
          try {
            document.body.classList.remove('skip-onboarding-active');
          } catch (e) {
            // no-op
          }
        }
      },
      immediate: true
    }
  }
};
</script>
